import { useCreateParty, useParties } from '../../api/hooks';
import { FormField, Select, TextInput } from '../../components/DesignSystem';

export const NEW_PARTY_OPTION = '__new-party';

export interface PartyFieldValue {
  /** '' (none), a party id, or NEW_PARTY_OPTION. */
  choice: string;
  newName: string;
}

export const EMPTY_PARTY_VALUE: PartyFieldValue = { choice: '', newName: '' };

interface PartyFieldProps {
  id: string;
  label?: string;
  value: PartyFieldValue;
  onChange: (value: PartyFieldValue) => void;
  allowNone?: boolean;
}

/** Party dropdown (from GET /parties) with an inline create-party option. */
export function PartyField({ id, label = 'Party', value, onChange, allowNone }: PartyFieldProps) {
  const parties = useParties({ limit: 100 });
  const options = parties.data?.data ?? [];

  return (
    <>
      <FormField htmlFor={id} label={label}>
        <Select
          id={id}
          onChange={(event) => onChange({ ...value, choice: event.target.value })}
          value={value.choice}
        >
          <option value="">{allowNone ? 'No party (unassigned)' : 'Select a party…'}</option>
          {options.map((party) => (
            <option key={party.id} value={party.id}>
              {party.name}
            </option>
          ))}
          <option value={NEW_PARTY_OPTION}>+ Create new party…</option>
        </Select>
      </FormField>
      {value.choice === NEW_PARTY_OPTION && (
        <FormField htmlFor={`${id}-new`} label="New Party Name">
          <TextInput
            id={`${id}-new`}
            onChange={(event) => onChange({ ...value, newName: event.target.value })}
            placeholder="e.g. Meridian Pictures Ltd"
            value={value.newName}
          />
        </FormField>
      )}
    </>
  );
}

/**
 * Resolves a PartyFieldValue to a party id, creating the party first when
 * the "new party" option was chosen. Returns undefined for "no party".
 */
export function usePartyResolver() {
  const createParty = useCreateParty();

  return async (value: PartyFieldValue): Promise<string | undefined> => {
    if (value.choice === '') {
      return undefined;
    }

    if (value.choice !== NEW_PARTY_OPTION) {
      return value.choice;
    }

    const created = await createParty.mutateAsync({ name: value.newName.trim() });
    return created.id;
  };
}
